import { useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Box } from '@mui/material'
import { ShoppingCartOutlined } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { getBomFeasibility, type OrderListItem, type BomFeasibilityLine } from '../../api/comenzi'
import PurchaseRequestFormDialog, { type PurchaseRequestPrefill } from '../achizitii/PurchaseRequestFormDialog'

const NEEDS_PURCHASE = 'Necesită achiziție'

export default function BomFeasibilityDialog({ target, onClose }: { target: OrderListItem | null; onClose: () => void }) {
  const open = target !== null
  const [requestPrefill, setRequestPrefill] = useState<PurchaseRequestPrefill | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['comercial', 'comenzi', target?.id, 'fezabilitate-bom'],
    queryFn: () => getBomFeasibility(target!.id),
    enabled: open,
  })

  // FR-ACA-003/FR-CMD-016 — propune cererea de achiziție pentru materia primă lipsă, fără reintroducere de date.
  // Închide acest dialog înainte de a deschide cererea, ca să nu se suprapună două modale.
  const missingPurchaseLines = data?.lines.filter((l) => l.suggestedAction === NEEDS_PURCHASE) ?? []
  const generatePurchaseRequest = (lines: BomFeasibilityLine[]) => {
    setRequestPrefill({
      notes: `Generat automat din verificarea de fezabilitate BOM pentru comanda ${target?.orderNumber}.`,
      lines: lines.map((l) => ({
        articleId: l.articleId, description: `Lipsă identificată la fezabilitate BOM — comanda ${target?.orderNumber}`,
        quantityRequested: String(l.shortfallQuantity), unitOfMeasureId: l.unitOfMeasureId,
      })),
    })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Fezabilitate BOM — {target?.orderNumber}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        )}
        {data && (
          <>
            <Alert severity={data.isFeasible ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {data.isFeasible
                ? 'Comanda e fezabilă integral din stocul liber curent — nicio componentă lipsă.'
                : 'Comanda nu e fezabilă integral din stocul liber curent — vezi componentele cu lipsă mai jos.'}
            </Alert>
            {data.criticalComponentCode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Constrângerea critică: {data.criticalComponentCode}
                {data.criticalComponentEstimatedDate && ` — disponibil estimat ${data.criticalComponentEstimatedDate}`}
              </Alert>
            )}
            {data.lines.length === 0 ? null : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Articol</TableCell>
                    <TableCell align="right">Necesar</TableCell>
                    <TableCell align="right">Disponibil</TableCell>
                    <TableCell align="right">Lipsă</TableCell>
                    <TableCell>Acțiune sugerată</TableCell>
                    <TableCell>Dată estimată</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((l) => (
                    <TableRow key={l.articleId} selected={l.articleId === data.criticalComponentArticleId}>
                      <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                      <TableCell align="right">{l.requiredQuantity.toFixed(3).replace('.', ',')}</TableCell>
                      <TableCell align="right">{l.availableQuantity.toFixed(3).replace('.', ',')}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{l.shortfallQuantity.toFixed(3).replace('.', ',')}</TableCell>
                      <TableCell>{l.suggestedAction}</TableCell>
                      <TableCell>{l.estimatedAvailabilityDate ?? '—'}</TableCell>
                      <TableCell>
                        {l.suggestedAction === NEEDS_PURCHASE && (
                          <Button size="small" startIcon={<ShoppingCartOutlined fontSize="small" />} onClick={() => generatePurchaseRequest([l])}>
                            Cerere achiziție
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        {missingPurchaseLines.length > 1 ? (
          <Button size="small" startIcon={<ShoppingCartOutlined fontSize="small" />} onClick={() => generatePurchaseRequest(missingPurchaseLines)}>
            Cerere achiziție pentru toate componentele lipsă ({missingPurchaseLines.length})
          </Button>
        ) : <span />}
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>

      <PurchaseRequestFormDialog target={requestPrefill ? 'new' : null} prefill={requestPrefill} onClose={() => setRequestPrefill(null)} />
    </Dialog>
  )
}
